import { DialogComponent, DialogOpenEvent, DialogCloseEvent } from '@theme/dialog';
import { CartAddEvent, ThemeEvents } from '@theme/events';
import { isMobileBreakpoint } from '@theme/utilities';

/**
 * A custom element that manages a cart drawer.
 *
 * @typedef {object} Refs
 * @property {HTMLDialogElement} dialog - The dialog element.
 *
 * @extends {DialogComponent}
 */
class CartDrawerComponent extends DialogComponent {
  /** @type {number} */
  #summaryThreshold = 0.5;

  /** @type {AbortController | null} */
  #historyAbortController = null;

  /** @type {ResizeObserver | null} */
  #stickyResizeObserver = null;

  /** @type {MutationObserver | null} */
  #stickyMutationObserver = null;

  /** @type {Element | null} */
  #observedStickySummary = null;

  /** @type {number} */
  #stickyUpdateFrame = 0;

  connectedCallback() {
    super.connectedCallback();
    document.addEventListener(CartAddEvent.eventName, this.#handleCartAdd);
    document.addEventListener(ThemeEvents.cartUpdate, this.#scheduleStickyUpdate);
    this.addEventListener(DialogOpenEvent.eventName, this.#updateStickyState);
    this.addEventListener(DialogOpenEvent.eventName, this.#handleHistoryOpen);
    this.addEventListener(DialogCloseEvent.eventName, this.#handleHistoryClose);
    window.addEventListener('resize', this.#scheduleStickyUpdate);
    window.visualViewport?.addEventListener('resize', this.#scheduleStickyUpdate);

    const { dialog } = /** @type {Refs} */ (this.refs);
    if (dialog) {
      this.#stickyResizeObserver = new ResizeObserver(this.#scheduleStickyUpdate);
      this.#stickyMutationObserver = new MutationObserver(this.#scheduleStickyUpdate);
      this.#stickyMutationObserver.observe(dialog, { childList: true, subtree: true, characterData: true });
    }

    if (history.state?.cartDrawerOpen) {
      history.replaceState(null, '');
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(CartAddEvent.eventName, this.#handleCartAdd);
    document.removeEventListener(ThemeEvents.cartUpdate, this.#scheduleStickyUpdate);
    this.removeEventListener(DialogOpenEvent.eventName, this.#updateStickyState);
    this.removeEventListener(DialogOpenEvent.eventName, this.#handleHistoryOpen);
    this.removeEventListener(DialogCloseEvent.eventName, this.#handleHistoryClose);
    window.removeEventListener('resize', this.#scheduleStickyUpdate);
    window.visualViewport?.removeEventListener('resize', this.#scheduleStickyUpdate);
    this.#stickyResizeObserver?.disconnect();
    this.#stickyMutationObserver?.disconnect();
    this.#observedStickySummary = null;
    cancelAnimationFrame(this.#stickyUpdateFrame);
    this.#stickyUpdateFrame = 0;
    this.#historyAbortController?.abort();
  }

  #handleHistoryOpen = () => {
    if (!isMobileBreakpoint()) return;

    if (!history.state?.cartDrawerOpen) {
      history.pushState({ cartDrawerOpen: true }, '');
    }

    this.#historyAbortController = new AbortController();
    window.addEventListener('popstate', this.#handlePopState, { signal: this.#historyAbortController.signal });
  };

  #handleHistoryClose = () => {
    this.#historyAbortController?.abort();
    if (history.state?.cartDrawerOpen) {
      history.back();
    }
  };

  #handlePopState = async () => {
    if (this.refs.dialog?.open) {
      this.refs.dialog.style.setProperty('--dialog-drawer-closing-animation', 'none');
      await this.closeDialog();
      this.refs.dialog.style.removeProperty('--dialog-drawer-closing-animation');
    }
  };

  #handleCartAdd = () => {
    if (this.hasAttribute('auto-open')) {
      this.showDialog();
    }
  };

  open() {
    this.showDialog();

    /**
     * Close cart drawer when installments CTA is clicked to avoid overlapping dialogs
     */
    customElements.whenDefined('shopify-payment-terms').then(() => {
      const installmentsContent = document.querySelector('shopify-payment-terms')?.shadowRoot;
      const cta = installmentsContent?.querySelector('#shopify-installments-cta');
      cta?.addEventListener('click', this.closeDialog, { once: true });
    });
  }

  close() {
    this.closeDialog();
  }

  #scheduleStickyUpdate = () => {
    cancelAnimationFrame(this.#stickyUpdateFrame);
    this.#stickyUpdateFrame = requestAnimationFrame(() => {
      this.#stickyUpdateFrame = 0;
      this.#updateStickyState();
    });
  };

  #updateStickyState() {
    const { dialog } = /** @type {Refs} */ (this.refs);
    if (!dialog || !dialog.open) return;

    // Refs do not cross nested `*-component` boundaries (e.g., `cart-items-component`), so we query within the dialog.
    const content = dialog.querySelector('.cart-drawer__content');
    const summary = dialog.querySelector('.cart-drawer__summary');

    if (summary !== this.#observedStickySummary) {
      this.#stickyResizeObserver?.disconnect();
      this.#stickyResizeObserver?.observe(dialog);
      if (summary) this.#stickyResizeObserver?.observe(summary);
      this.#observedStickySummary = summary;
    }

    if (!content || !summary) {
      // Ensure the dialog doesn't get stuck in "unsticky" mode when summary disappears (e.g., empty cart).
      dialog.setAttribute('cart-summary-sticky', 'false');
      return;
    }

    const drawerHeight = dialog.getBoundingClientRect().height;
    const summaryHeight = summary.getBoundingClientRect().height;
    const ratio = summaryHeight / drawerHeight;
    dialog.setAttribute('cart-summary-sticky', ratio > this.#summaryThreshold ? 'false' : 'true');
  }
}

if (!customElements.get('cart-drawer-component')) {
  customElements.define('cart-drawer-component', CartDrawerComponent);
}
